import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, PlayCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const FIELDS = [
  { key: 'price', label: 'Monthly price ($)', dollars: true, from: 'price_cents' },
  { key: 'sms_included', label: 'Texts included' },
  { key: 'sms_overage', label: '$ per extra text', dollars: true, from: 'sms_overage_cents' },
  { key: 'ai_included', label: 'AI requests included' },
  { key: 'ai_overage', label: '$ per extra AI request', dollars: true, from: 'ai_overage_cents' },
];

const toForm = (p) => ({
  price: (p.price_cents / 100).toFixed(2),
  sms_included: String(p.sms_included ?? 0),
  sms_overage: (p.sms_overage_cents / 100).toFixed(2),
  ai_included: String(p.ai_included ?? 0),
  ai_overage: (p.ai_overage_cents / 100).toFixed(2),
});
const cents = (v) => Math.round(parseFloat(v || '0') * 100);

/** Company Admin: each plan's price, what it includes, and the price of each text / AI request over it. */
export default function PlanPricingCard({ plans, token, onSaved }) {
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState(null);
  const [running, setRunning] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    setForms(Object.fromEntries((plans || []).filter((p) => p.code).map((p) => [p.code, toForm(p)])));
  }, [plans]);

  const save = async (code) => {
    const f = forms[code];
    const body = {
      price_cents: cents(f.price), sms_included: parseInt(f.sms_included || '0', 10),
      ai_included: parseInt(f.ai_included || '0', 10), sms_overage_cents: cents(f.sms_overage),
      ai_overage_cents: cents(f.ai_overage),
    };
    if (Object.values(body).some((v) => Number.isNaN(v) || v < 0)) {
      toast.error('Enter amounts of 0 or more');
      return;
    }
    setSaving(code);
    try {
      await axios.put(`${API}/api/admin/billing/plans/${code}`, body, auth);
      toast.success(`${code} saved. New invoices use it; issued invoices keep their lines.`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the plan');
    } finally {
      setSaving(null);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/api/admin/billing/run-cycle`, {}, auth);
      const n = Object.keys(res.data.report || {}).length;
      toast.success(n ? `Autopay ran for ${n} restaurant(s)` : 'Autopay ran: nothing due right now');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Autopay run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="bg-white border-0 shadow-sm" data-testid="plan-pricing">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-base">Plans and usage pricing</CardTitle>
          <CardDescription>
            Autopay charges each restaurant's card on file one day before the due date. Texts and AI requests over the plan are itemized on the invoice.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={runNow} disabled={running} data-testid="run-autopay">
          <PlayCircle className="h-4 w-4 mr-2" />Run autopay now
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {(plans || []).filter((p) => p.code).map((p) => (
          <div key={p.code} className="border rounded-lg p-3" data-testid={`plan-pricing-${p.code}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold">{p.name} <span className="text-xs text-gray-500">({p.code})</span></p>
              <Button size="sm" onClick={() => save(p.code)} disabled={saving === p.code} data-testid={`plan-save-${p.code}`}>
                <Save className="h-4 w-4 mr-1" />Save
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {FIELDS.map((fd) => (
                <label key={fd.key} className="text-xs text-gray-600">
                  {fd.label}
                  <Input
                    inputMode="decimal"
                    value={forms[p.code]?.[fd.key] ?? ''}
                    onChange={(e) => setForms((all) => ({ ...all, [p.code]: { ...all[p.code], [fd.key]: e.target.value } }))}
                    data-testid={`plan-${p.code}-${fd.key}`}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
